import { useState } from 'react';
import { api } from '@halo/web-lib';
import { Loader2, AlertTriangle, X } from 'lucide-react';

interface OwnedStore { id: number; name: string }

interface Props {
  readonly userId: string;
  readonly email: string;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

interface OwnedStoresData { ownedStores: OwnedStore[] }

function extractOwnedStores(value: unknown): OwnedStore[] | null {
  if (!value || typeof value !== 'object') return null;
  const maybe = (value as { ownedStores?: unknown }).ownedStores;
  if (!Array.isArray(maybe)) return null;
  const stores: OwnedStore[] = [];
  for (const item of maybe) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { id?: unknown }).id === 'number' &&
      typeof (item as { name?: unknown }).name === 'string'
    ) {
      stores.push({ id: (item as OwnedStore).id, name: (item as OwnedStore).name });
    }
  }
  return stores;
}

export function DeleteUserModal({ userId, email, onClose, onSuccess }: Props) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownedStores, setOwnedStores] = useState<OwnedStore[] | null>(null);

  const canConfirm = typed === email && !submitting;

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.delete<OwnedStoresData | { id: string }>(
        `/api/v1/admin/users/${userId}`,
        { confirmEmail: typed },
      );

      if (res.success) {
        onSuccess();
        return;
      }

      const stores = extractOwnedStores(res.data);
      if (stores && stores.length > 0) {
        setOwnedStores(stores);
        return;
      }
      setError(res.error ?? 'Failed to delete user');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {ownedStores ? 'Cannot delete user' : 'Delete user'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {ownedStores ? (
            <>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This user owns the following stores. Reassign or delete them first.
              </p>
              <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                {ownedStores.map((s) => (
                  <li key={s.id}>{s.name} (#{s.id})</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This permanently deletes <strong>{email}</strong> and revokes all sessions.
                The action cannot be undone.
              </p>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Type the user's email to confirm:
                </span>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={email}
                  className="mt-1 w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  autoFocus
                />
              </label>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            {ownedStores ? 'Close' : 'Cancel'}
          </button>
          {!ownedStores && (
            <button
              onClick={handleDelete}
              disabled={!canConfirm}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Delete user
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
